import { PROVIDER_TYPE } from 'constants/global';
import { initMercadoPago } from 'utils/mercadopago';

const importers = {
  bbva: () => import('components/BBVA'),
  mercadoPago: () => import('components/MercadoPago'),
  paymentReference: () => import('components/PaymentReference'),
};

const moduleKeyByProvider = {
  [PROVIDER_TYPE.BANCOMER]: 'bbva',
  [PROVIDER_TYPE.MERCADO_PAGO]: 'mercadoPago',
  [PROVIDER_TYPE.PAYCASH]: 'paymentReference',
  [PROVIDER_TYPE.PUNTORED]: 'paymentReference',
};

const moduleCache = {};

const unknownProviderError = (provider) => new Error(`Unknown payment provider: ${provider}`);

/**
 * Loads (once) the ES module of the component that renders the given provider.
 * A failed import is evicted from the cache so a later call can retry.
 * @param {string} provider - One of PROVIDER_TYPE.
 * @returns {Promise<{ default: Function }>} Module namespace with a default export.
 */
export function loadPaymentMethodModule(provider) {
  const key = moduleKeyByProvider[provider];

  if (!key) {
    return Promise.reject(unknownProviderError(provider));
  }

  if (!moduleCache[key]) {
    moduleCache[key] = importers[key]().catch((error) => {
      delete moduleCache[key];
      throw error;
    });
  }

  return moduleCache[key];
}

/**
 * Activates a payment method on demand: loads its component module and, for
 * Mercado Pago, injects the SDK and creates its singleton (memoized by
 * initMercadoPago itself, so retries after a failure still work).
 * @param {string} provider - One of PROVIDER_TYPE.
 * @returns {Promise<void>}
 */
export async function activatePaymentMethod(provider) {
  await loadPaymentMethodModule(provider);

  if (provider === PROVIDER_TYPE.MERCADO_PAGO) {
    await initMercadoPago();
  }
}
